import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext, useMemo, useState } from "react";
import { Alert, ButtonToolbar, Col, Row } from "react-bootstrap";
import { useParams } from "react-router-dom";
import type { ContactsController } from "../../../../api/src/contacts/contacts.controller";
import type { Contact } from "../../../../api/src/contacts/lib";
import type {
  SerializedObject,
  UnpackResponse,
  WrappedApiResponse,
} from "../../../../api/src/lib";
import type {
  EditableTask,
  EditableWorkflow,
} from "../../../../api/src/tasks/tasks.service";
import Loading from "../../Loading";
import { NotificationContext } from "../../Notifications";
import ActionButton from "../../components/ActionButton";
import MobileActionsMenu, {
  MobileActionItem,
} from "../../components/MobileActionsMenu";
import CreateTaskPane from "../../components/CreateTaskPane";
import CreateWorkflowPane from "../../components/CreateWorkflowPane";
import FormError from "../../components/FormError";
import { NoteForm, NotesPane } from "../../components/Notes";
import {
  useAuthenticatedFetch,
  useAuthenticatedMutationAsync,
} from "../../lib/api";
import { deserializeNote } from "../../lib/notes";
import { deserializeTask, deserializeWorkflow } from "../../tasks/lib";
import { useQueryHousehold } from "../household/lib";
import ContactArchiveDialog from "./ContactArchiveDialog";
import PersonForm from "./PersonForm";
import TrustForm from "./TrustForm";
import {
  contactErrorMessage,
  isContactNotFound,
  retryUnlessContactNotFound,
  useUnarchiveContact,
} from "./lib";

const ContactInfo = () => {
  const { contactId } = useParams();

  const {
    isPending,
    error: contactError,
    data: contactBody,
  } = useAuthenticatedFetch<
    WrappedApiResponse<
      Contact & { linkedHousehold?: { id?: number; name?: string } }
    >
  >(`/v1/contacts/${contactId}`, undefined, {
    retry: retryUnlessContactNotFound,
  });
  const contact = contactBody?.data;

  const linkedToWealthbox = contact?.idExternal.wealthbox != null;
  const linkedToRedtail = contact?.idExternal.redtail != null;
  const linkedToSlant = contact?.idExternal.slant != null;
  const linkedToCrm = linkedToWealthbox || linkedToRedtail || linkedToSlant;

  // Tasks follow the HOUSEHOLD's CRM link, not the contact's. A contact can
  // carry two links at once — the Wealthbox-to-Slant switch inserts
  // `contact_slant_contact` without deleting `contact_wealthbox_contact` — while
  // its household carries exactly one, so the household is the unambiguous
  // signal (s-task-assignee-list-source-019fd301825de871, R1). Same hop
  // `AccountHeader` already makes for an account.
  const taskHouseholdId = contact?.household?.id;
  const { data: taskHousehold } = useQueryHousehold(taskHouseholdId ?? 0, {
    enabled: taskHouseholdId != null,
  });

  // A professional contact has no household to inherit from. Its existing
  // contact-level behaviour is left exactly as it was rather than a rule being
  // invented for it — an open question, see the PR.
  const taskCrm =
    taskHouseholdId == null
      ? {
          wealthbox: linkedToWealthbox,
          redtail: linkedToRedtail,
          slant: linkedToSlant,
        }
      : {
          wealthbox: taskHousehold?.idExternal.wealthbox != null,
          redtail: taskHousehold?.idExternal.redtail != null,
          slant: taskHousehold?.idExternal.slant != null,
        };
  // Workflows are only implemented for Wealthbox and Redtail; Slant has no
  // workflow templates or create path, so hide the action for Slant-only links.
  const supportsWorkflows = linkedToWealthbox || linkedToRedtail;

  const queryClient = useQueryClient();

  const [showNotes, setShowNotes] = useState(false);
  const onShowNotes = useCallback(() => {
    setShowNotes(!showNotes);
  }, [showNotes]);

  const createNote = useAuthenticatedMutationAsync<
    SerializedObject<UnpackResponse<ContactsController["createNote"]>>
  >(`/v1/contacts/${contactId}/notes`, async (data: NoteForm) => ({
    method: "POST",
    body: JSON.stringify(data),
  }));

  const onCreateNote = useCallback(
    async (data: NoteForm) => {
      const body = await createNote(data);
      queryClient.setQueryData([`/v1/contacts/${contactId}`], {
        data: { ...contact, notes: [...(contact?.notes ?? []), body.data] },
      });
      return body.data === null ? null : deserializeNote(body.data);
    },
    [contact, contactId, createNote, queryClient],
  );

  const [isShowCreateTask, setIsShowCreateTask] = useState(false);
  const onShowCreateTask = useCallback(() => {
    setIsShowCreateTask(!isShowCreateTask);
  }, [isShowCreateTask]);

  const createTaskMutation = useAuthenticatedMutationAsync<
    SerializedObject<UnpackResponse<ContactsController["createTaskForContact"]>>
  >(`/v1/contacts/${contactId}/tasks`, async (data: EditableTask) => ({
    method: "POST",
    body: JSON.stringify(data),
  }));

  const onCreateTask = useCallback(
    async (data: EditableTask) => {
      const body = await createTaskMutation(data);

      if (body !== null) {
        queryClient.setQueryData([`/v1/contacts/${contactId}`], {
          data: { ...contact, tasks: [...(contact?.tasks ?? []), body.data] },
        });
      }

      return body.data === null ? null : deserializeTask(body.data);
    },
    [contact, contactId, createTaskMutation, queryClient],
  );

  const [isShowCreateWorkflow, setIsShowCreateWorkflow] = useState(false);
  const showCreateWorkflow = useCallback(() => {
    setIsShowCreateWorkflow(!isShowCreateWorkflow);
  }, [isShowCreateWorkflow]);

  const createWorkflow = useAuthenticatedMutationAsync<
    SerializedObject<
      UnpackResponse<ContactsController["createWorkflowForContact"]>
    >
  >(`/v1/contacts/${contactId}/workflows`, async (data: EditableWorkflow) => ({
    method: "POST",
    body: JSON.stringify(data),
  }));

  const onCreateWorkflow = useCallback(
    async (data: EditableWorkflow) => {
      const body = await createWorkflow(data);

      if (body.data === null) {
        return null;
      } else {
        queryClient.setQueryData([`/v1/contacts/${contactId}`], {
          data: {
            ...contact,
            workflows: [...(contact?.workflows ?? []), body.data],
          },
        });

        return deserializeWorkflow(body.data);
      }
    },
    [createWorkflow, queryClient, contactId, contact],
  );

  const [showModal, setShowModal] = useState(false);
  const handleArchiveClick = useCallback(() => {
    setShowModal(true);
  }, []);

  const { mutateAsync: unarchiveContact } = useUnarchiveContact(
    parseInt(contactId ?? "-1"),
  );

  const notificationContext = useContext(NotificationContext);

  const handleUnarchiveContact = useCallback(async () => {
    try {
      await unarchiveContact();

      setShowModal(false);
      notificationContext.pushNotification({
        id: `contact-${contactId}`,
        header: "Contact Unarchived",
        body: `${contact?.name} unarchived`,
        variant: "success",
      });
    } catch (ex) {
      console.error("Failed to unarchive contact", ex);
      notificationContext.pushNotification({
        id: `contact-${contactId}`,
        header: "Failed to Unarchive Contact",
        body: `${contact?.name} was not unarchived`,
        variant: "danger",
      });
    }
  }, [contact?.name, contactId, notificationContext, unarchiveContact]);

  const contactMobileActions = useMemo(() => {
    const actions: MobileActionItem[] = [];
    if (linkedToCrm) {
      actions.push(
        {
          label: "Create Task",
          icon: "/icons/tasks.svg",
          onClick: onShowCreateTask,
        },
        ...(supportsWorkflows
          ? [
              {
                label: "Create Workflow",
                icon: "/icons/flowchart.svg",
                onClick: showCreateWorkflow,
              },
            ]
          : []),
        {
          label: "Notes",
          icon: "/icons/folded-list.svg",
          onClick: onShowNotes,
        },
      );
    }
    actions.push(
      contact?.isActive
        ? {
            label: "Archive",
            icon: "/icons/trash.svg",
            onClick: handleArchiveClick,
          }
        : {
            label: "Unarchive",
            icon: "/icons/trash.svg",
            onClick: handleUnarchiveContact,
          },
    );
    return actions;
  }, [
    linkedToCrm,
    supportsWorkflows,
    onShowCreateTask,
    showCreateWorkflow,
    onShowNotes,
    contact?.isActive,
    handleArchiveClick,
    handleUnarchiveContact,
  ]);

  const externalURLWealthbox =
    contact?.idExternal.wealthbox == null
      ? undefined
      : `${process.env.REACT_APP_WEALTHBOX_APP_ROOT}/contacts/${contact?.idExternal.wealthbox}`;
  const externalURLRedtail =
    contact?.idExternal.redtail == null
      ? undefined
      : `${process.env.REACT_APP_REDTAIL_APP_ROOT}/contacts/${contact?.idExternal.redtail}`;
  const externalURLSlant =
    // `== null`: both are `string | null` (APP-1653), and `typeof null` is
    // `"object"`, so a `typeof … === "undefined"` test would build the link
    // with "null" in it.
    contact?.idExternal.slant == null ||
    contact?.slantBookId == null ||
    contact?.slantHouseholdId == null ||
    !process.env.REACT_APP_SLANT_APP_ROOT
      ? undefined
      : `${process.env.REACT_APP_SLANT_APP_ROOT}/${contact.slantBookId}/clients/${contact.slantHouseholdId}/people/${contact.idExternal.slant}`;

  return isPending ? (
    <Loading />
  ) : isContactNotFound(contactError) ? (
    <FormError
      message={contactErrorMessage(contactError) ?? "Contact not found"}
    />
  ) : !contact ? (
    <FormError message="Failed to load contact details" />
  ) : (
    <>
      <Row>
        <Col className="d-flex justify-content-between">
          <h1>Contact Details</h1>
          {typeof externalURLWealthbox === "undefined" ? null : (
            <a
              className="mt-auto mb-auto"
              href={externalURLWealthbox}
              rel="noreferrer"
              target="_blank"
            >
              View in Wealthbox
            </a>
          )}
          {typeof externalURLRedtail === "undefined" ? null : (
            <a
              className="mt-auto mb-auto"
              href={externalURLRedtail}
              rel="noreferrer"
              target="_blank"
            >
              View in Redtail
            </a>
          )}
          {typeof externalURLSlant === "undefined" ? null : (
            <a
              className="mt-auto mb-auto"
              href={externalURLSlant}
              rel="noreferrer"
              target="_blank"
            >
              View in Slant
            </a>
          )}
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="d-flex justify-content-end mb-3 d-lg-none">
            <MobileActionsMenu actions={contactMobileActions} />
          </div>
          <ButtonToolbar className="d-none d-lg-flex mb-3 justify-content-end">
            {!linkedToCrm ? null : (
              <>
                <ActionButton
                  variant="secondary"
                  icon="/icons/tasks.svg"
                  label="Create Task"
                  type="button"
                  onClick={onShowCreateTask}
                  className="me-2"
                />
                {supportsWorkflows && (
                  <ActionButton
                    variant="secondary"
                    icon="/icons/flowchart.svg"
                    label="Create Workflow"
                    type="button"
                    onClick={showCreateWorkflow}
                    className="me-2"
                  />
                )}
                <ActionButton
                  variant="secondary"
                  icon="/icons/folded-list.svg"
                  label="Notes"
                  type="button"
                  onClick={onShowNotes}
                  className="me-2"
                />
              </>
            )}
            {contact.isActive ? (
              <ActionButton
                type="button"
                label="Archive"
                icon="/icons/trash.svg"
                onClick={handleArchiveClick}
              />
            ) : (
              <ActionButton
                type="button"
                label="Unarchive"
                icon="/icons/trash.svg"
                onClick={handleUnarchiveContact}
              />
            )}
          </ButtonToolbar>
        </Col>
        <ContactArchiveDialog
          showModal={showModal}
          setShowModal={setShowModal}
          archiveContactId={contactId ? Number(contactId) : 0}
          archiveContactName={contact?.name ?? ""}
        />
      </Row>
      {contact.type === "person" ? (
        <PersonForm contact={contact} />
      ) : contact.type === "trust" || contact.type === "organization" ? (
        <TrustForm contact={contact} />
      ) : (
        <Alert variant="warning">Unable to display contact</Alert>
      )}
      <CreateTaskPane
        show={isShowCreateTask}
        setShow={setIsShowCreateTask}
        onCreate={onCreateTask}
        crm={taskCrm}
        record={
          taskHouseholdId == null ? undefined : { householdId: taskHouseholdId }
        }
      />
      <CreateWorkflowPane
        show={isShowCreateWorkflow}
        setShow={setIsShowCreateWorkflow}
        onCreate={onCreateWorkflow}
      />
      <NotesPane
        showNotes={showNotes}
        setShowNotes={setShowNotes}
        notes={contact?.notes ?? []}
        onCreate={onCreateNote}
      />
    </>
  );
};

export default ContactInfo;
